import { AsyncAction, portalAction, PortalThunk, postTag, updateTag, tagsSelector, setIsBiometricEnabledOnServer, setBiometricLoginStatus } from '../'
import { get, parseCreationOptionsFromJSON, parseRequestOptionsFromJSON } from '@github/webauthn-json/browser-ponyfill'
import { formatISO } from 'date-fns'
import {
  BiometricFinishAuthenticationRequest,
  BiometricLoginStatus,
  BiometricStartAuthenticationResponse,
  BIOMETRIC_DENIED_COUNT_KEY,
  BIOMETRIC_LAST_DENIED_DATE_KEY,
  GetConsumerCredentialsResponse,
  StartRegistrationResponseData,
} from '../../model'
import { getBiometricSettings, getCredentialIds, setBiometricSettings } from '@afterpay/utils/biometric'
import { EMPTY_ALLOW_CREDENTIALS_ERROR, NULL_ALLOW_CREDENTIALS_ERROR } from '../../component/LoginPassword/constants'
import { getUsername } from '../../component/LoginPassword/utils'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { startBiometricRegistration, finishBiometricRegistration } from './api'

export const getConsumerCredentials: PortalThunk<void, GetConsumerCredentialsResponse> = portalAction('consumers/fido2/credentials/get', async (_, thunk) => {
  return await thunk.extra.portalApi().get('/portal/consumers/fido2/credentials')
})

export const cleanUpConsumerCredentials =
  (email: string | undefined, mobile: string | undefined): AsyncAction<void> =>
  async (dispatch) => {
    const [storedCredentialIdsByEmail, storedCredentialIdsByMobile] = getCredentialIds(email, mobile)

    const storedCredentials = Array.from(new Set([...storedCredentialIdsByEmail, ...storedCredentialIdsByMobile]))

    if (storedCredentials.length === 0) {
      return
    }

    const remoteCredentials = await dispatch(getConsumerCredentials()).unwrap()
    const remoteCredentialIds = remoteCredentials.map((credential) => credential.id)

    const validCredentialIds = storedCredentials.filter((credential) => remoteCredentialIds.includes(credential))

    const biometricSettings = getBiometricSettings()

    if (valueIsNotNullOrUndefined(email)) {
      biometricSettings.credentials[email.toLowerCase()] = validCredentialIds
    }
    if (valueIsNotNullOrUndefined(mobile)) {
      biometricSettings.credentials[mobile] = validCredentialIds
    }

    setBiometricSettings(biometricSettings)
  }

export const setLocalBiometricConsumerCredentials = (
  email: string | undefined,
  mobile: string | undefined,
  storedCredentialIdsByEmail: string[],
  storedCredentialIdsByMobile: string[],
  credentialIds: string[]
): void => {
  const biometricSettings = getBiometricSettings()

  storedCredentialIdsByEmail.push(...credentialIds)
  storedCredentialIdsByMobile.push(...credentialIds)

  if (email !== null && email !== undefined) {
    biometricSettings.credentials[email.toLowerCase()] = Array.from(new Set([...storedCredentialIdsByEmail]))
  }

  if (mobile !== null && mobile !== undefined) {
    biometricSettings.credentials[mobile] = Array.from(new Set([...storedCredentialIdsByMobile]))
  }

  setBiometricSettings(biometricSettings)
}

export const getStartRegistrationResponseData = (): AsyncAction<StartRegistrationResponseData> => async (dispatch) => {
  const startBiometricRegistrationData = await dispatch(startBiometricRegistration()).unwrap()
  const creationOptions = parseCreationOptionsFromJSON({ publicKey: startBiometricRegistrationData.publicKeyCredentialCreationOptions })

  return {
    startBiometricRegistrationData,
    creationOptions,
  }
}

export const getFinishBiometricRegistrationCredentialIds =
  (requestId: string, credential: any): AsyncAction<string[]> =>
  async (dispatch) => {
    await dispatch(finishBiometricRegistration({ requestId, credential })).unwrap()

    return [credential.id]
  }

export const dismissBiometricRegistrationModal = (): AsyncAction<number> => async (dispatch, getState) => {
  const { consumerTag } = tagsSelector(getState())
  const hasDeniedCountTag = BIOMETRIC_DENIED_COUNT_KEY in consumerTag
  const hasLastDeniedDateTag = BIOMETRIC_LAST_DENIED_DATE_KEY in consumerTag
  const deniedCountValue = consumerTag[BIOMETRIC_DENIED_COUNT_KEY]
  const currCount = deniedCountValue == null ? NaN : parseInt(deniedCountValue, 10)
  const deniedCount = Number.isNaN(currCount) ? 1 : currCount + 1
  const deniedCountAction = hasDeniedCountTag ? updateTag : postTag
  const lastDeniedDateAction = hasLastDeniedDateTag ? updateTag : postTag

  dispatch(deniedCountAction({ name: BIOMETRIC_DENIED_COUNT_KEY, data: `${deniedCount}` }))
  dispatch(lastDeniedDateAction({ name: BIOMETRIC_LAST_DENIED_DATE_KEY, data: formatISO(new Date(), { representation: 'date' }) }))

  return deniedCount
}

const BASE_URL = '/portal/consumers/fido2/authenticate'

export const startBiometricAuthentication: PortalThunk<string, BiometricStartAuthenticationResponse> = portalAction(
  'biometric/startAuthentication',
  async (username, thunk) => {
    const payload = {
      username,
    }
    return await thunk.extra.portalApi().post(BASE_URL, payload)
  }
)

export const finishBiometricAuthentication: PortalThunk<BiometricFinishAuthenticationRequest, any> = portalAction(
  'biometric/finishAuthentication',
  async (request, thunk) => {
    return await thunk.extra.portalApi().post(`${BASE_URL}/finish`, request)
  }
)

export const checkIsBiometricEnabledOnServer =
  (email: string | undefined, mobile: string | undefined, isMobileLogin: boolean): AsyncAction<boolean> =>
  async (dispatch) => {
    const username = getUsername(isMobileLogin, mobile, email)

    try {
      const startResult = await dispatch(startBiometricAuthentication(username)).unwrap()
      const allowCredentials = startResult.publicKeyCredentialRequestOptions?.allowCredentials

      const isEnabled = Array.isArray(allowCredentials) && allowCredentials.length > 0
      dispatch(setIsBiometricEnabledOnServer(isEnabled))

      return isEnabled
    } catch (e) {
      console.error('Error authenticating biometrics on the server', e)
      return false
    }
  }

export const biometricLogin =
  (email: string | undefined, mobile: string | undefined, isMobileLogin: boolean): AsyncAction<void> =>
  async (dispatch) => {
    dispatch(setBiometricLoginStatus(BiometricLoginStatus.Started))

    const username = getUsername(isMobileLogin, mobile, email)

    const startResult = await dispatch(startBiometricAuthentication(username)).unwrap()
    const allowCredentials = startResult.publicKeyCredentialRequestOptions?.allowCredentials
    if (Array.isArray(allowCredentials) && allowCredentials.length === 0) {
      throw new Error(EMPTY_ALLOW_CREDENTIALS_ERROR)
    }

    if (allowCredentials === null) {
      throw new Error(NULL_ALLOW_CREDENTIALS_ERROR)
    }

    const [storedCredentialIdsByEmail, storedCredentialIdsByMobile] = getCredentialIds(email, mobile)

    const storedCredentialIds = Array.from(new Set([...storedCredentialIdsByEmail, ...storedCredentialIdsByMobile]))

    const allowedCredentialIds = allowCredentials?.map((credential) => credential.id) ?? []

    const validCredentialInSettings = allowedCredentialIds.some((credentialId) => storedCredentialIds.includes(credentialId))

    const options = parseRequestOptionsFromJSON({ publicKey: startResult.publicKeyCredentialRequestOptions })

    const credential = await get(options)

    await dispatch(
      finishBiometricAuthentication({
        requestId: startResult.requestId,
        credential: credential as PublicKeyCredential,
      })
    ).unwrap()

    if (!validCredentialInSettings) {
      setLocalBiometricConsumerCredentials(email, mobile, storedCredentialIdsByEmail, storedCredentialIdsByMobile, [credential.id])
    }
  }
